import React, { useState, useEffect } from 'react';
import {
  LuX,
  LuBot,
  LuDatabase,
  LuGlobe,
  LuWebhook,
  LuServer,
  LuCpu,
  LuSettings,
  LuAlertCircle,
  LuPlay,
  LuFileText,
  LuNetwork,
  LuActivity
} from 'react-icons/lu';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';

// Import tab components
import Deployments from './PanelTabs/Deployments';
import Settings from './PanelTabs/Settings';
import Logs from './PanelTabs/Logs';
import Architecture from './PanelTabs/Architecture';
import Usage from './PanelTabs/Usage';

const NodeDetailsPanel = ({ node, onClose, moduleName, institution }) => {
  const [config, setConfig] = useState({});
  const [loading, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState('deployments');

  useEffect(() => {
    if (node?.data?.config) {
      setConfig(node.data.config);
    }
  }, [node]);

  // Handle panel visibility animations
  useEffect(() => {
    if (node) {
      // Slide in from right
      setIsVisible(true);
      setIsClosing(false);
      setActiveTab('settings'); // Reset to default tab when opening
    }
  }, [node]);

  const handleClose = () => {
    // Trigger slide out animation
    setIsClosing(true);
    setIsVisible(false);

    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 300); // Match the transition duration
  };

  const getIconFromValue = (iconValue) => {
    switch (iconValue) {
      case 'LuDatabase':
        return <LuDatabase className="w-5 h-5" />;
      case 'LuBot':
        return <LuBot className="w-5 h-5" />;
      case 'LuGlobe':
        return <LuGlobe className="w-5 h-5" />;
      case 'LuWebhook':
        return <LuWebhook className="w-5 h-5" />;
      case 'LuServer':
        return <LuServer className="w-5 h-5" />;
      case 'LuCpu':
        return <LuCpu className="w-5 h-5" />;
      default:
        return <LuBot className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'configured':
        return 'bg-green-500';
      case 'not_configured':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTabIcon = (tabName) => {
    switch (tabName) {
      case 'deployments':
        return <LuPlay className="w-4 h-4" />;
      case 'settings':
        return <LuSettings className="w-4 h-4" />;
      case 'logs':
        return <LuFileText className="w-4 h-4" />;
      case 'architecture':
        return <LuNetwork className="w-4 h-4" />;
      case 'usage':
        return <LuActivity className="w-4 h-4" />;
      default:
        return <LuSettings className="w-4 h-4" />;
    }
  };

  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'settings', label: 'Settings' }
    ];

    const nodeType = node?.data?.type;

    switch (nodeType) {
      case 'worker':
        return [
          { id: 'settings', label: 'Settings' },
          { id: 'deployments', label: 'Deploy' }
        ];
      case 'database':
        return baseTabs;
      case 'server':
        return baseTabs;
      default:
        return baseTabs;
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (!institution?.id || !moduleName || !node?.id) return;

    try {
      setSaving(true);
      setError(null);

      const nodeRef = doc(db, `colleges/${institution.id}/configs/${moduleName}/nodes`, node.id);
      await updateDoc(nodeRef, {
        config: config,
        status: 'configured',
        lastModified: new Date()
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving node config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!institution?.id || !moduleName || !node?.id) return;
    if (!window.confirm('Are you sure you want to delete this node?')) return;

    try {
      setSaving(true);
      setError(null);

      const nodeRef = doc(db, `colleges/${institution.id}/configs/${moduleName}/nodes`, node.id);
      await deleteDoc(nodeRef);

      handleClose();
    } catch (err) {
      console.error('Error deleting node:', err);
      setError('Failed to delete node');
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    const tabProps = {
      node,
      config,
      onConfigChange: handleConfigChange,
      loading,
      error,
      onSave: handleSave,
      onDelete: handleDelete,
      saved,
      moduleName,
      institution
    };

    switch (activeTab) {
      case 'deployments':
        return <Deployments {...tabProps} />;
      case 'settings':
        return <Settings {...tabProps} />;
      case 'logs':
        return <Logs {...tabProps} />;
      case 'architecture':
        return <Architecture {...tabProps} />;
      case 'usage':
        return <Usage {...tabProps} />;
      default:
        return <Deployments {...tabProps} />;
    }
  };

  if (!node) return null;

  const availableTabs = getAvailableTabs();

  return (
    <div
      className={`w-1/2 absolute h-full p-4 top-0 right-0 z-10 transition duration-300 ease-in-out ${
        isVisible ? 'translate-x-0 opacity-100' : isClosing ? 'translate-x-1/2 opacity-0' : 'translate-x-1/2 opacity-0'
      }`}
      style={{
        transform: isVisible ? 'translateX(0%)' : 'translateX(50%)'
      }}
    >
      <div className="h-full w-full bg-[#ffffff10] overflow-hidden relative border border-[#ffffff15] rounded-xl backdrop-blur-3xl flex flex-col">
        {/* Header */}
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-0">
            <div className="flex items-center gap-3">
              <div className="text-[#e1e1e1]">
                {getIconFromValue(node.data.icon)}
              </div>
              <div>
                <h2 className="text-white font-medium text-lg">{node.data.label}</h2>
                <p className="text-white/60 text-sm capitalize">{node.data.type} Node</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="h-8 w-8 group flex items-center justify-center cursor-pointer border border-[#ffffff10] hover:border-[#ffffff15] bg-[#ffffff05] transition rounded hover:bg-[#ffffff10]"
            >
              <LuX className="text-[#b1b1b1] group-hover:text-[#d1d1d1] w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-[#ffffff15] flex-shrink-0">
          <div className="flex space-x-1">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-white border-white'
                    : 'text-white/60 border-transparent hover:text-white/80'
                }`}
              >
                {getTabIcon(tab.id)}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {renderTabContent()}
        </div>

        {/* Global Error Message */}
        {error && (
          <div className="p-4 border-t border-[#ffffff15] bg-[#ffffff05] flex-shrink-0">
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <LuAlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeDetailsPanel;